import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Rnd } from "react-rnd";
import styled, { css } from "styled-components";
import debounce from "lodash/debounce";
import { Button } from "components/bootstrap";
import Icon from "components/common/Icon";
import zIndices from "theme/z-indices";
const DEFAULT_SIZE = { width: 450, height: 400 };
const DEFAULT_STRING_SIZE = { width: DEFAULT_SIZE.width.toString(), height: DEFAULT_SIZE.height.toString() };
const halfWidth = Math.ceil(window.innerWidth / 2 - DEFAULT_SIZE.width / 2);
const halfHeight = Math.ceil(window.innerHeight / 2 - DEFAULT_SIZE.height / 2);
const stayOnScreenHeight = halfHeight < 0 ? 55 : halfHeight;
const DEFAULT_POSITION = {
  x: halfWidth,
  y: stayOnScreenHeight
};
const InteractableModalWrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndices.modalBody};
  pointer-events: none;
`;
const StyledRnd = styled(Rnd)(
  ({ theme }) => css`
    box-shadow:
      0 0 9px ${theme.colors.global.navigationBoxShadow},
      0 0 6px ${theme.colors.global.navigationBoxShadow},
      0 0 3px ${theme.colors.global.navigationBoxShadow};
    background-color: ${theme.colors.global.contentBackground};
    border: 1px solid ${theme.colors.variant.lightest.default};
    border-radius: 3px;
    flex-direction: column;
    display: flex !important;
    pointer-events: auto;
  `
);
const Content = styled.div`
  flex: 1;
  padding: 0 15px;
`;
const Header = styled.header(
  ({ theme }) => css`
    padding: 6px 12px 9px;
    display: flex;
    align-items: center;
    background-color: ${theme.colors.variant.lightest.default};
    border-bottom: 1px solid ${theme.colors.variant.lighter.default};
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    cursor: move;
  `
);
const Title = styled.h3(
  ({ theme }) => css`
    color: ${theme.colors.text.primary};
    flex: 1;
  `
);
const DragBars = styled(Icon)(
  ({ theme }) => css`
    color: ${theme.colors.variant.darker.default};
    margin-right: 9px;
  `
);
const CloseButton = styled(Button)(
  ({ theme }) => css`
    && {
      color: ${theme.colors.variant.light.default};

      &:hover {
        color: ${theme.colors.variant.default};
      }
    }
  `
);
const InteractableModal = ({
  children = void 0,
  className = void 0,
  minHeight = DEFAULT_SIZE.height,
  minWidth = DEFAULT_SIZE.width,
  onClose = () => {
  },
  onDrag = () => {
  },
  onResize = () => {
  },
  position = DEFAULT_POSITION,
  size = DEFAULT_STRING_SIZE,
  title = "",
  wrapperClassName = void 0
}) => {
  const { t } = useTranslation("common");
  const dragHandleRef = useRef(null);
  const [dragHandleClassName, setDragHandleClassName] = useState(null);
  const [dragPosition, setDragPosition] = useState(position);
  const [resizeSize, setResizeSize] = useState(size);
  const handleDragStop = (_event, { x, y }) => {
    setDragPosition({ x, y });
    onDrag({ x, y });
  };
  const handleResizeStop = (_event, direction, ref) => {
    const newSize = {
      width: ref.style.width,
      height: ref.style.height
    };
    let newCoords = { ...dragPosition };
    switch (direction) {
      case "left":
      case "topLeft":
      case "top":
        newCoords = {
          x: dragPosition.x - (parseFloat(ref.style.width) - parseFloat(resizeSize.width)),
          y: dragPosition.y - (parseFloat(ref.style.height) - parseFloat(resizeSize.height))
        };
        break;
      case "bottomLeft":
        newCoords = {
          x: dragPosition.x - (parseFloat(ref.style.width) - parseFloat(resizeSize.width)),
          y: dragPosition.y
        };
        break;
      case "topRight":
        newCoords = {
          x: dragPosition.x,
          y: dragPosition.y - (parseFloat(ref.style.height) - parseFloat(resizeSize.height))
        };
        break;
      default:
        break;
    }
    setResizeSize(newSize);
    onResize(newSize);
    handleDragStop(null, newCoords);
  };
  const handleBrowserResize = debounce(() => {
    const { x: currentX, y: currentY } = dragPosition;
    const { width, height } = resizeSize;
    const { innerWidth, innerHeight } = window;
    const boundingBox = {
      top: 0,
      bottom: parseFloat(height),
      left: 0,
      right: parseFloat(width)
    };
    const modalXWithNewWidth = innerWidth - boundingBox.right;
    const modalYWithNewHeight = innerHeight - boundingBox.bottom;
    const newCoords = {
      x: Math.max(Math.min(modalXWithNewWidth, currentX), boundingBox.left),
      y: Math.max(Math.min(modalYWithNewHeight, currentY), boundingBox.top)
    };
    handleDragStop(null, newCoords);
  }, 150);
  useEffect(() => {
    setDragHandleClassName(dragHandleRef.current.classList[0]);
  }, []);
  useEffect(() => {
    window.addEventListener("resize", handleBrowserResize, false);
    return () => {
      window.removeEventListener("resize", handleBrowserResize);
    };
  }, [dragPosition, handleBrowserResize]);
  return /* @__PURE__ */ React.createElement(InteractableModalWrapper, { className: wrapperClassName, role: "dialog" }, /* @__PURE__ */ React.createElement(
    StyledRnd,
    {
      default: { ...position, ...size },
      minHeight,
      minWidth,
      maxHeight: window.innerHeight,
      maxWidth: window.innerWidth,
      dragHandleClassName,
      onDragStop: handleDragStop,
      onResizeStop: handleResizeStop,
      position: dragPosition,
      size: resizeSize,
      className,
      bounds: "window"
    },
    /* @__PURE__ */ React.createElement(Header, { ref: dragHandleRef }, /* @__PURE__ */ React.createElement(Title, null, /* @__PURE__ */ React.createElement(DragBars, { name: "drag_indicator" }), title), /* @__PURE__ */ React.createElement(CloseButton, { bsStyle: "link", onClick: onClose, bsSize: "small", title: t("actions.close") }, /* @__PURE__ */ React.createElement(Icon, { name: "close", size: "lg" }))),
    /* @__PURE__ */ React.createElement(Content, null, children)
  ));
};
export default InteractableModal;
